import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { Search, ShieldCheck, Loader2, CheckCircle2, XCircle, AlertTriangle, HelpCircle, Mail, Users } from "lucide-react";

interface Workspace {
  id: string;
  name: string;
  ownerEmail?: string;
  neverbounceManualEnabled?: boolean;
}

interface VerifyResult {
  total: number;
  sources: string[];
  summary: {
    valid: number;
    invalid: number;
    disposable: number;
    catchall: number;
    unknown: number;
  };
}

type Source = "leads" | "clients" | "marketing" | "talent" | "talentContacts";

const SOURCE_OPTIONS: { id: Source; label: string; description: string }[] = [
  { id: "leads",         label: "Leads",              description: "Prospective clients in your leads pipeline" },
  { id: "clients",       label: "Clients",            description: "Booked and active client accounts" },
  { id: "marketing",     label: "Marketing Contacts", description: "Media contacts and audience subscribers" },
  { id: "talent",        label: "Talent",             description: "Artist / performer contact emails" },
  { id: "talentContacts",label: "Talent Contacts",    description: "Per-talent address book (crew, venues, promoters)" },
];

const ALL_SOURCES: Source[] = SOURCE_OPTIONS.map((s) => s.id);

const RESULT_META: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  valid:      { label: "Valid",      icon: <CheckCircle2 className="h-4 w-4" />, color: "text-green-600 dark:text-green-400" },
  catchall:   { label: "Catch-all",  icon: <AlertTriangle className="h-4 w-4" />, color: "text-yellow-600 dark:text-yellow-400" },
  invalid:    { label: "Invalid",    icon: <XCircle className="h-4 w-4" />, color: "text-destructive" },
  disposable: { label: "Disposable", icon: <XCircle className="h-4 w-4" />, color: "text-destructive" },
  unknown:    { label: "Unknown",    icon: <HelpCircle className="h-4 w-4" />, color: "text-muted-foreground" },
};

export default function PlatformEmailVerify() {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<Record<string, VerifyResult>>({});
  const [running, setRunning] = useState<Set<string>>(new Set());
  const [selectedSources, setSelectedSources] = useState<Set<Source>>(new Set(ALL_SOURCES));

  const { data: workspaces = [], isLoading } = useQuery<Workspace[]>({
    queryKey: ["/api/platform/workspaces"],
  });

  const verifyMutation = useMutation({
    mutationFn: (workspaceId: string) =>
      apiRequest("POST", `/api/platform/workspaces/${workspaceId}/verify-emails`, {
        sources: [...selectedSources],
      }).then((r) => r.json()),
    onMutate: (workspaceId) => {
      setRunning((prev) => new Set([...prev, workspaceId]));
    },
    onSuccess: (data, workspaceId) => {
      setRunning((prev) => {
        const next = new Set(prev);
        next.delete(workspaceId);
        return next;
      });
      setResults((prev) => ({ ...prev, [workspaceId]: data }));
      const invalid = (data.summary?.invalid ?? 0) + (data.summary?.disposable ?? 0);
      toast({
        title: "Verification complete",
        description: `${data.total} emails checked — ${invalid} flagged as invalid/disposable.`,
        variant: invalid > 0 ? "destructive" : "default",
      });
    },
    onError: (_err, workspaceId) => {
      setRunning((prev) => {
        const next = new Set(prev);
        next.delete(workspaceId);
        return next;
      });
      toast({ title: "Verification failed", description: "Check server logs for details.", variant: "destructive" });
    },
  });

  // Task #244 — toggle the manual single-send NeverBounce tier perk per workspace.
  const perkMutation = useMutation({
    mutationFn: ({ workspaceId, enabled }: { workspaceId: string; enabled: boolean }) =>
      apiRequest("PATCH", `/api/platform/workspaces/${workspaceId}/neverbounce-manual`, { enabled }).then((r) => r.json()),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/workspaces"] });
      toast({
        title: vars.enabled ? "Manual verification enabled" : "Manual verification disabled",
        description: vars.enabled
          ? "This workspace can now verify single recipients on demand in the composer."
          : "On-demand single-recipient verification is now off for this workspace.",
      });
    },
    onError: () => {
      toast({ title: "Update failed", description: "Could not change the perk. Check server logs.", variant: "destructive" });
    },
  });

  const allChecked = selectedSources.size === ALL_SOURCES.length;
  const someChecked = selectedSources.size > 0 && !allChecked;

  function toggleAll() {
    setSelectedSources(allChecked ? new Set() : new Set(ALL_SOURCES));
  }

  function toggleSource(source: Source) {
    setSelectedSources((prev) => {
      const next = new Set(prev);
      if (next.has(source)) {
        next.delete(source);
      } else {
        next.add(source);
      }
      return next;
    });
  }

  const filtered = (workspaces as any[]).filter((ws: any) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (ws.name || "").toLowerCase().includes(q) ||
      (ws.ownerEmail || "").toLowerCase().includes(q) ||
      (ws.id || "").toLowerCase().includes(q)
    );
  });

  const sourceSummaryLabel = allChecked
    ? "All sources"
    : selectedSources.size === 0
    ? "No sources selected"
    : SOURCE_OPTIONS.filter((s) => selectedSources.has(s.id)).map((s) => s.label).join(", ");

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-xl font-semibold">Email Verification</h1>
          <p className="text-sm text-muted-foreground">
            Run NeverBounce verification on selected contact sources in a workspace. Results are cached for 30 days and displayed as red-dot indicators in the app.
          </p>
        </div>
      </div>

      {/* Source selector */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">Contact Sources to Scan</CardTitle>
          </div>
          <CardDescription className="text-xs">
            Choose which types of contacts to include in the scan. Selected: <span className="font-medium text-foreground">{sourceSummaryLabel}</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-3">
            {/* Select All row */}
            <div className="flex items-center gap-3 pb-2 border-b">
              <Checkbox
                id="source-all"
                data-testid="checkbox-source-all"
                checked={allChecked}
                ref={(el) => {
                  if (el) (el as any).indeterminate = someChecked;
                }}
                onCheckedChange={toggleAll}
              />
              <Label htmlFor="source-all" className="font-medium cursor-pointer text-sm">
                Select All
              </Label>
            </div>
            {/* Individual sources */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {SOURCE_OPTIONS.map((source) => (
                <div key={source.id} className="flex items-start gap-3">
                  <Checkbox
                    id={`source-${source.id}`}
                    data-testid={`checkbox-source-${source.id}`}
                    checked={selectedSources.has(source.id)}
                    onCheckedChange={() => toggleSource(source.id)}
                    className="mt-0.5"
                  />
                  <div>
                    <Label htmlFor={`source-${source.id}`} className="font-medium cursor-pointer text-sm leading-none">
                      {source.label}
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">{source.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          data-testid="input-workspace-search"
          placeholder="Search workspaces…"
          className="pl-9"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-center text-muted-foreground py-12">No workspaces found.</p>
      ) : (
        <div className="space-y-3">
          {filtered.map((ws: any) => {
            const isRunning = running.has(ws.id);
            const result = results[ws.id];
            const invalid = result
              ? (result.summary?.invalid ?? 0) + (result.summary?.disposable ?? 0)
              : 0;

            return (
              <Card key={ws.id} className="overflow-hidden">
                <CardHeader className="py-4 px-5">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <CardTitle className="text-base truncate">{ws.name || ws.id}</CardTitle>
                      {ws.ownerEmail && (
                        <CardDescription className="truncate">{ws.ownerEmail}</CardDescription>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0" title="Manual single-send NeverBounce — tier perk">
                      <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" />
                      <Label htmlFor={`perk-${ws.id}`} className="text-xs text-muted-foreground cursor-pointer hidden md:inline">
                        Manual verify
                      </Label>
                      <Switch
                        id={`perk-${ws.id}`}
                        data-testid={`switch-manual-verify-${ws.id}`}
                        checked={ws.neverbounceManualEnabled === true}
                        disabled={perkMutation.isPending}
                        onCheckedChange={(checked) => perkMutation.mutate({ workspaceId: ws.id, enabled: checked })}
                      />
                    </div>
                    <Button
                      data-testid={`button-verify-${ws.id}`}
                      size="sm"
                      variant={result && invalid > 0 ? "destructive" : result ? "outline" : "default"}
                      disabled={isRunning || selectedSources.size === 0}
                      onClick={() => verifyMutation.mutate(ws.id)}
                      className="shrink-0"
                    >
                      {isRunning ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                          Verifying…
                        </>
                      ) : result ? (
                        "Re-run"
                      ) : (
                        <>
                          <Mail className="h-3.5 w-3.5 mr-1.5" />
                          Verify Emails
                        </>
                      )}
                    </Button>
                  </div>
                </CardHeader>

                {result && (
                  <CardContent className="pb-4 px-5 pt-0 border-t">
                    <div className="flex flex-wrap gap-4 mt-3">
                      <span className="text-sm text-muted-foreground">
                        <strong>{result.total}</strong> total
                      </span>
                      {result.sources && result.sources.length < ALL_SOURCES.length && (
                        <Badge variant="secondary" className="text-xs font-normal">
                          {SOURCE_OPTIONS.filter((s) => result.sources.includes(s.id)).map((s) => s.label).join(", ")}
                        </Badge>
                      )}
                      {Object.entries(result.summary)
                        .filter(([, count]) => count > 0)
                        .sort(([a], [b]) => {
                          const order = ["invalid", "disposable", "catchall", "unknown", "valid"];
                          return order.indexOf(a) - order.indexOf(b);
                        })
                        .map(([key, count]) => {
                          const meta = RESULT_META[key];
                          return (
                            <span
                              key={key}
                              className={`flex items-center gap-1 text-sm font-medium ${meta?.color || ""}`}
                            >
                              {meta?.icon}
                              {count} {meta?.label || key}
                            </span>
                          );
                        })}
                    </div>
                    {invalid > 0 && (
                      <p className="mt-2 text-xs text-muted-foreground">
                        Red-dot indicators are now visible in the leads spreadsheet for {invalid} invalid/disposable address{invalid !== 1 ? "es" : ""}. These addresses will also be suppressed from cold email and drip campaigns.
                      </p>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {selectedSources.size === 0 && (
        <p className="text-center text-sm text-muted-foreground py-2">
          Select at least one source above to enable scanning.
        </p>
      )}
    </div>
  );
}
